import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { formatDate, formatMoney, formatNumber, formatQty } from '../../../utils/format'

// 🔴 Das Trennzeichen der Hausform ist ein NBSP, kein normales Leerzeichen:
// `utils/format.js` haelt es als `NBSP` und setzt es in `formatUnit`, `formatMoney`
// und `formatBytes` zwischen Wert und Einheit. Ein normales Leerzeichen erlaubte
// einen Umbruch zwischen Wert und Einheit und waere nicht mehr byte-gleich zur
// Tabellenzelle (Regel-16-W1).
const NBSP = ' '

// 🔴 Eine Modulkonstante, kein Literal: der leere Fall ist der haeufigste, und ein
// frisches `[]` je Render liesse jeden Verbraucher, der `ignoredValues` in
// `useEffect`-Abhaengigkeiten legt, in eine Schleife laufen (Regel-15-W-B).
const LEER = []
const LEERGEMELDET = { url: null, werte: LEER }

/**
 * useListQuery — the ONE source of a list's query state.
 *
 * @description
 *   Holds tabs, search, filter dimensions, sort and page for a list view, reads
 *   and writes them in the URL and hands Z3 its chip descriptions and "Clear all"
 *   its base state. It replaces the hand-rolled `useSearchParams` logic that ten
 *   files write and fifteen read (concept §10.2 N10.1) — there is ONE state model,
 *   not a second beside it.
 *
 *   The URL is the source (N10.1). Every change writes it; nothing is kept in
 *   `localStorage` (OE-6 = A, concept §10.2 N10.6). Reading is defensive: an
 *   unknown value is DISCARDED — the hook removes it from the URL (`replace`, so
 *   no extra history entry) and reports it in `ignoredValues` for that pass —
 *   never a silent fallback (N10.4). Only keys the hook owns are touched; foreign
 *   URL parameters stay.
 *
 *   History (N10.2): a tab or page change pushes an entry, everything else
 *   replaces — so Back from a detail returns to the SAME state, and Back from the
 *   list returns to the previous page instead of walking through every keystroke.
 *   Every query change resets the page to 1 (N10.3).
 *
 *   Two `set` calls in ONE turn compose: the hook carries the pending state until
 *   the router has caught up, so `set('status', ['open'])` and `set('q', 'burr')`
 *   in the same handler both land (N10.3's "and clears the selection" needs it).
 *
 *   🔴 `all` is the explicit "no restriction" value and is DISTINCT from a
 *   narrowing default: an absent parameter means "the default applies"
 *   (`resolved=hidden`), `resolved=all` means "show everything" (concept §10.1).
 *   The two must stay apart, otherwise removing a default chip in Z3 (§4.3,
 *   "Wert wird alle") would be a no-op that re-applies the default.
 *
 *   🔴 The marker applies ONLY where there IS a closed option list. Free text
 *   (a search field, `values` omitted) has no list to hold `all`, so `?q=all` is
 *   the search term "all", not "no restriction" — otherwise a user searching for
 *   "all" would silently see the unfiltered list, with no chip and no notice.
 *
 *   `state` is flat: one key per dimension, plus `tab`, `sort`, `page` and
 *   `pageSize`. Those four names are reserved — a dimension may not use them, and
 *   the hook throws if one does. Every dimension needs a `label` (the chip has to
 *   name it) — a missing one throws rather than printing `undefined` on screen.
 *
 *   Unrelated URL parameters are PRESERVED: the hook only touches the keys it
 *   owns, so a view can carry its own parameter beside the query.
 *
 *   🔴 **Mehrfach-Dimensionen werden AUCH WIEDERHOLT gelesen (G5a, 2026-10-14),
 *   geschrieben wird KOMMA-GETRENNT.** Für `kind: 'multi'`/`'quick'` liest
 *   `leseWert` ALLE Werte des Parameters (`params.getAll(key)`, je Wert
 *   zusätzlich `split(',')`) — nicht nur den ersten. Der Anlass ist ein lebender
 *   Deep-Link: `frontend/src/home/components/UrgencyRow.jsx:128` öffnet
 *   `/rma?status=open&status=approved&status=received`, und mit `get()` wären
 *   `approved` und `received` STILL verschwunden (gültige Werte, nur nicht
 *   gelesen — kein Chip, keine Meldung). Geschrieben wird weiterhin EIN
 *   Parameter mit Komma (`schreibeWert`), also sind beide Formen lesbar und nur
 *   eine wird erzeugt. Für eine einfache URL ist `getAll` dasselbe wie `get`.
 *   `single`/`switch`/`date`/`number` bleiben bei `get` — dort gewinnt der
 *   erste Wert, unverändert.
 *
 * @useWhen
 *   - A list view with tabs, a search, filter dimensions, a sort or a page
 *   - Building Z3 (the active-filter row) — `chips` is its content
 *   - A "Clear all" control — `clearAll` is its contract
 *
 * @dontUseFor
 *   - A single value that is not part of a list query (a detail-page tab) — plain `useSearchParams`
 *   - Client-only state that must NOT be shareable (a hover, a draft) — that is `useState`
 *
 * @related
 *   - `ActiveFilterBar` (BS3) — renders `chips` and calls `clearAll`
 *   - `ListToolbar` (BS2) — takes `state`/`set` as the query contract
 *   - `useOverflowMask` (BS15) — the other G0.1 hook
 *
 * @reuseHints
 *   - The nine list views without URL state (`/products`, `/vendors`, `/media`, …)
 *   - Any view that today keeps a filter in `localStorage` — the value becomes a visible default
 *
 * @param {object} config
 * @param {Array} [config.dimensions] One entry per filter dimension:
 *   `{ key, kind, default, values, label, unit, format, keepUnknown }`. `kind` is
 *   `'single' | 'multi' | 'quick' | 'date' | 'number' | 'switch' | 'period'`.
 *   `values` is the
 *   closed option list — entries are a raw value or `{ value, label }`. Omit it
 *   for free text (a search field), which then accepts anything; 🔴 a FACET
 *   without `values` checks nothing and reports nothing — the option list is part
 *   of the contract, not a decoration. `label` is REQUIRED (the chip's name).
 *   🔴 `keepUnknown` (optional, default `false`; G5a-Nachbesserung 5, 2026-10-14,
 *   R16 W5) — for a `multi`/`quick` dimension whose option list is a SUBSET of
 *   the value space (users can be deleted, so `responsible` never lists every
 *   id a case can carry): a value the list does not know is KEPT instead of
 *   discarded, and the chip names it `… (unknown)` (`etikett`). The `all` marker
 *   keeps its meaning either way.
 *
 *   🔴 **W1 (2026-10-15): „ohne den Schalter ist das Verhalten unverändert" galt
 *   für die vier heutigen Verbraucher, nicht allgemein.** Gemessen an allen vier
 *   Importeuren (`POOverview` `OPEN_STATUS_VALUES ⊆ STATUS_VALUES`,
 *   `ProductsTable` `ACTIVE_STATUSES ⊆ STATUS_OPTIONS`, `ListenMuster` ohne
 *   Vorgabe an einer Liste-Dimension, `RMAOverviewLayout` `'hidden' ∈
 *   RESOLVED_OPTIONS`) — **kein Lebendverbraucher ändert sein Verhalten**
 *   (unbekannte Werte werden entfernt und gemeldet, `ignoredValues`, N10.4).
 *   Der eine Wert aber, der `leseWert` OHNE den Schalter passiert, ist die
 *   **VORGABE** (`vorgabe(d)` → `d.default`): eine Dimension
 *   `{kind:'single', values:['a','b'], default:'c'}` rendert heute „X: c
 *   (unknown)", vorher „X: c". Wer eine solche Dimension baut, liest den Chip
 *   als Aussage — nicht als Fehler.
 *
 *   🔴 **W2 (2026-10-15): die Klammer steht am ENDE der Werteliste, nicht an
 *   jedem unbekannten Wert.** `useListQuery.test.js` L.15c pinnt „Responsible:
 *   7, 42 (unknown)" — die Klammer steht hinter dem LETZTEN Wert und liest sich
 *   damit optisch als Urteil über beide; `42` ist der unbekannte. Der
 *   handgerollte Streifen vor G5a schrieb die Kennung einzeln („#42 (unknown)").
 *   Kein Defekt (die Aussage stimmt), aber eine benannte Unschärfe; eindeutiger
 *   wäre „42 (unknown), 7" oder eine Klammer je unbekanntem Wert.
 *   `format` picks the HOUSE helper for a `number` chip and is one of
 *   `'number' | 'qty' | 'money'` (default `'number'`): `number` → `formatNumber`,
 *   `qty` → `formatQty` (a quantity has no decimals: `Qty: ≤ 50`), `money` →
 *   `formatMoney` (value, NBSP and currency — `Amount: ≥ 1.234,50 €`). An unknown
 *   `format` throws. `unit` is optional and hangs on the value with the house
 *   separator NBSP (`Amount: ≥ 50,00 €`); for `money` it is the currency CODE
 *   (`'USD'` → `99,00 USD`), default `EUR`.
 *   `key` may not be `tab`, `sort`, `page` or `pageSize`.
 * @param {object} [config.sort]   `{ default, values }` — URL value `feld` (asc) or `-feld` (desc)
 * @param {object|Array} [config.tabs] `{ default, values }` or a plain array of keys (first = default)
 * @param {number} [config.pageSize] Page size, carried through in `state` (default 50)
 * @returns {{ state: object, set: Function, clear: Function, clearAll: Function,
 *             chips: Array, isDefault: boolean, ignoredValues: Array }}
 *   `isDefault` is true while NO list parameter (page, tab, sort, search, filter)
 *   differs from its default. It is NOT the signal for Z3 / "Clear all" — that is
 *   `chips.length > 0` (concept §4.1/§4.3): the frame (a `period` segment) is not
 *   a chip but does move `isDefault`.
 *   `clearAll(options?)` resets every dimension to its default and drops `page` and
 *   `sort`. `tab` is dropped too — UNLESS `options.keepTab` is true, for a view whose
 *   `tab` is the VIEW and not a filter (a body that does not own `tab` would otherwise
 *   jump back to the first tab). `ActiveFilterBar` calls `onClearAll?.()` without an
 *   argument, so the caller passes the wrapper: `onClearAll={() => clearAll({ keepTab: true })}`.
 *   `ignoredValues` lists `{ key, value, param, rest }` for the pass in which the
 *   value was still in the URL; the parameter is removed from the URL in the same
 *   pass (`rest` is the corrected value where the parameter carries a list, `null`
 *   where the whole parameter goes). 🔴 The notice belongs to ONE cleanup, not to
 *   a URL string: it stands while the cleaned URL is on screen and is dropped for
 *   good once that URL is left — reaching the same clean URL later does not bring
 *   it back. It does NOT flicker: the pass that follows the cleanup reports the
 *   same value as the cleanup pass itself. One pass runs ahead of the cleanup —
 *   the view stands on the default while the address bar still carries the bad
 *   value (a write during render is forbidden), and that pass is not a second
 *   notice. The hook does NOT know the total number of rows: a view clamps `page`
 *   against its hit count to the last page (N10.4).
 */
export default function useListQuery({ dimensions = [], sort, tabs, pageSize = 50 } = {}) {
  pruefeKonfiguration(dimensions)

  const [searchParams, setSearchParams] = useSearchParams()

  // 🔴 Die Abhaengigkeit ist der SERIALISIERTE Zustand, nicht die Identitaet der
  // Objekte: ein Aufrufer gibt `dimensions` als Array-Literal je Render neu, die
  // Identitaet wechselt also bei jedem Render. Waere sie die Abhaengigkeit, waere
  // die Memoisierung wertlos und `state` bei jedem Render ein neues Objekt — jeder
  // Verbraucher, der es in `useEffect`-Abhaengigkeiten legt, liefe in eine Schleife.
  const schluessel = useMemo(
    () => JSON.stringify({
      dims: dimensions.map(d => [d.key, d.kind, d.default ?? null, d.values ?? null, d.label ?? null, d.unit ?? null, d.format ?? null, d.keepUnknown ?? null]),
      sort: normalisiereAchse(sort),
      tabs: normalisiereAchse(tabs),
      pageSize,
    }),
    [dimensions, sort, tabs, pageSize],
  )

  // 🔴 Und die Achsen selbst werden ebenso INHALTSBASIERT stabilisiert — sonst
  // endet die Vorkehrung eine Funktion zu frueh: `set` haengt an `dimensions`,
  // `chips` an `set`, und beim erklarten Normalfall (Literal je Render) waere
  // beides bei JEDEM Render neu. `schluessel` traegt genau diesen Inhalt.
  const { dims, reiter, sortierung } = useMemo(
    () => ({ dims: dimensions, reiter: normalisiereAchse(tabs), sortierung: normalisiereAchse(sort) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `schluessel` ist der serialisierte Inhalt von `dimensions`/`sort`/`tabs`; die drei werden nur beim Wechsel dieses Inhalts neu gelesen
    [schluessel],
  )

  const { state, ignoriert } = useMemo(() => {
    const ign = []
    const zustand = {
      tab: leseAchse(searchParams, reiter, 'tab', ign),
      sort: leseAchse(searchParams, sortierung, 'sort', ign),
      page: leseSeite(searchParams),
      pageSize,
    }
    dims.forEach(d => { zustand[d.key] = leseWert(d, searchParams, ign) })
    return { state: zustand, ignoriert: ign }
  }, [searchParams, dims, reiter, sortierung, pageSize])

  // 🔴 N10.4 „verworfen" heisst ENTFERNT, nicht nur gemeldet: bliebe der Wert in
  // der URL stehen, truege ein geteilter Link dauerhaft einen Wert, der nicht
  // gilt, und der Hinweis erschiene bei jedem Aufruf. Entfernt wird per `replace`
  // — eine Bereinigung ist kein Verlaufsschritt. Gemeldet bleibt der Durchlauf, in
  // dem der Wert noch in der URL stand (sonst waere der Hinweis einen Render lang
  // sichtbar und verschwaende wieder).
  const [gemeldet, setGemeldet] = useState(LEERGEMELDET)

  useEffect(() => {
    // 🔴 Die Meldung gehoert EINEM Bereinigungsvorgang, nicht einer URL-Zeichenkette
    // (Regel-15-W-A): sie steht, solange die bereinigte URL zu sehen ist, und faellt
    // endgueltig weg, sobald diese URL verlassen wird. Ohne das kaeme sie wieder,
    // wenn der Nutzer spaeter zufaellig genau dieselbe saubere URL erreicht — und
    // behauptete dann einen Verlust, den es nicht gab.
    if (ignoriert.length === 0) {
      if (gemeldet.url != null && searchParams.toString() !== gemeldet.url) setGemeldet(LEERGEMELDET)
      return
    }
    const naechste = new URLSearchParams(searchParams)
    // `rest` traegt den bereinigten Wert des Parameters: eine Mehrfachauswahl ist
    // EINE Liste, dort faellt nur der ungueltige Wert weg — die gueltigen bleiben.
    ignoriert.forEach(e => {
      if (e.rest == null || e.rest === '') naechste.delete(e.param)
      else naechste.set(e.param, e.rest)
    })
    setGemeldet({ url: naechste.toString(), werte: ignoriert })
    setSearchParams(naechste, { replace: true })
  }, [ignoriert, searchParams, setSearchParams, gemeldet.url])

  const ignoredValues = ignoriert.length > 0
    ? ignoriert
    : (gemeldet.url === searchParams.toString() ? gemeldet.werte : LEER)

  // 🔴 Zwei `set` in EINEM Zug duerfen sich nicht ueberschreiben. Die funktionale
  // Form von `setSearchParams` genuegt dafuer NICHT: react-router ruft sie mit dem
  // `searchParams` DIESES Renderns auf, zwei Aufrufe im selben Handler sehen damit
  // denselben Stand und der zweite faengt den ersten ein. Der Hook haelt den
  // zuletzt geschriebenen Stand deshalb selbst, bis der Router nachgezogen ist.
  const zugRef = useRef(null)
  useEffect(() => { zugRef.current = null }, [searchParams])

  const set = useCallback((key, wert) => {
    const istAchse = key === 'tab' || key === 'sort' || key === 'page'
    const d = istAchse ? null : dims.find(x => x.key === key)
    if (!istAchse && !d) {
      // Ein unbekannter Schluessel ist ein Programmierfehler, kein Zustand: er
      // wird gemeldet statt still verschluckt (N10.4), bleibt aber ein No-op.
      if (process.env.NODE_ENV !== 'production') {
        console.error(`useListQuery: unbekannter Schluessel "${key}" in set() — er gehoert zu keiner Dimension und ist keine Achse.`)
      }
      return
    }
    setSearchParams(prev => {
      const naechste = new URLSearchParams(zugRef.current ?? prev)
      if (key === 'tab') {
        schreibeAchse(naechste, 'tab', wert, reiter)
      } else if (key === 'sort') {
        schreibeAchse(naechste, 'sort', wert, sortierung)
      } else if (key === 'page') {
        schreibeSeite(naechste, wert)
      } else {
        schreibeWert(naechste, d, normalisiere(d, wert))
      }
      // N10.3: jede Abfrageaenderung setzt die Seite auf 1 — ausser der Seitenwechsel selbst.
      if (key !== 'page') naechste.delete('page')
      zugRef.current = naechste
      return naechste
    }, { replace: key !== 'tab' && key !== 'page' })
  }, [setSearchParams, dims, reiter, sortierung])

  const clear = useCallback(key => set(key, null), [set])

  // 🔴 `keepTab` (UI2 Teil 10, G11b): eine Ansicht, deren `tab` ein Rumpf NICHT
  // besitzt, darf ihn beim „Clear all" nicht verlieren — auf `/blog-sources`
  // spränge der Reiter sonst von „Topics Inbox" auf „Sources", obwohl der Reiter
  // die ANSICHT ist und kein Filter (Plan §3.2). Vorgabe `false` = altes
  // Verhalten; kein bestehender Aufrufer aendert sich.
  // ⚠️ `ActiveFilterBar` ruft `onClearAll?.()` OHNE Argument — der Aufrufer muss
  // die Hülle uebergeben (`onClearAll={() => clearAll({ keepTab: true })}`),
  // nicht `clearAll` direkt.
  const clearAll = useCallback((optionen = {}) => {
    setSearchParams(prev => {
      const naechste = new URLSearchParams(zugRef.current ?? prev)
      dims.forEach(d => schreibeWert(naechste, d, vorgabe(d)))
      if (!optionen.keepTab) naechste.delete('tab')
      naechste.delete('sort')
      naechste.delete('page')
      zugRef.current = naechste
      return naechste
    }, { replace: true })
  }, [setSearchParams, dims])

  const chips = useMemo(
    () => baueChips(dims, state, set),
    [dims, state, set],
  )

  const isDefault = useMemo(
    () => state.tab === reiter.default
      && state.sort === sortierung.default
      && state.page === 1
      && dims.every(d => gleich(state[d.key], vorgabe(d))),
    [state, reiter, sortierung, dims],
  )

  return { state, set, clear, clearAll, chips, isDefault, ignoredValues }
}

// ── Konfiguration ──────────────────────────────────────────────────────────

const RESERVIERT = ['tab', 'sort', 'page', 'pageSize']

/** Die geschlossene Menge der Chip-Formen fuer `kind: 'number'` (Regel-16-K1). */
const FORMATE = ['number', 'qty', 'money']

/**
 * Ein Konfigurationsfehler wird LAUT, nicht sichtbar-falsch: eine Dimension ohne
 * `label` schriebe das Literal `undefined` in die Oberflaeche, ein reservierter
 * Schluessel ueberschriebe den Zustand des Hooks selbst (die Schleife laeuft NACH
 * dem Aufbau), und ein unbekanntes `format` fiele still auf `number` zurueck —
 * ein Tippfehler saehe dann aus wie eine Absicht. Hausform wie
 * `EmptyState`/`ResultCount.noun`.
 */
function pruefeKonfiguration(dimensions) {
  dimensions.forEach(d => {
    if (d.label == null || d.label === '') {
      throw new Error(
        `useListQuery: die Dimension "${d.key}" hat kein \`label\` — ohne es kann der Chip sie nicht benennen.`,
      )
    }
    if (RESERVIERT.includes(d.key)) {
      throw new Error(
        `useListQuery: "${d.key}" ist ein reservierter Schluessel (${RESERVIERT.join(', ')}) und kann keine Dimension sein.`,
      )
    }
    if (d.format != null && !FORMATE.includes(d.format)) {
      throw new Error(
        `useListQuery: die Dimension "${d.key}" hat ein unbekanntes \`format\` "${d.format}" — erlaubt sind ${FORMATE.join(', ')}.`,
      )
    }
  })
}

/** `tabs`/`sort` duerfen eine Liste (erster Wert = Vorgabe) oder ein Objekt sein. */
function normalisiereAchse(achse) {
  if (!achse) return { default: null, values: [] }
  if (Array.isArray(achse)) return { default: achse[0] ?? null, values: achse.slice() }
  return { default: achse.default ?? null, values: (achse.values ?? []).slice() }
}

function rohWerte(d) {
  return (d.values ?? []).map(v => (typeof v === 'string' ? v : v.value))
}

/** Traegt die Dimension eine geschlossene Werteliste, gegen die ein Wert aufloesen KANN? */
function hatListe(d) {
  return Array.isArray(d.values) && d.values.length > 0
}

function etikett(d, roh) {
  const e = (d.values ?? []).find(v => (typeof v === 'string' ? v : v.value) === roh)
  // 🔴 GEFUNDEN heisst „die Liste kennt den Wert" — und dann ist er NIE
  // „(unknown)", auch wenn er als blosse Zeichenkette in `values` steht (die
  // Hausform `values: ['open', 'waiting']`): dort IST die Zeichenkette das
  // Etikett. Der Zweig war bis zur G5a-Nachbesserung 5 still — er fiel auf
  // `roh` zurueck, was fuer eine Zeichenkette dasselbe ist; mit dem
  // `(unknown)`-Zusatz darunter waere aus „Status: open" ein „Status: open
  // (unknown)" geworden (gemessen: 6 rote Proben in `useListQuery.test.js`).
  if (e !== undefined) return typeof e === 'string' ? e : (e.label ?? e.value)
  // 🔴 R16 W5 (G5a-Nachbesserung 5, 2026-10-14): loest die Werteliste die
  // Kennung nicht auf, sagt der Chip das AUS — sonst stuende „Responsible: 42"
  // ueber einer leeren Liste, und der Bediener hielte den Filter fuer kaputt.
  // Das Backend liefert bei unbekannter Kennung bewusst NICHTS statt alles
  // (`RMAOverviewLayout` vor G5a, Wortlaut im alten Aktivfilter-Streifen):
  // die leere Menge ist die richtige Antwort, und der Chip muss den Grund
  // nennen.
  // ⚠️ Freitext (kein `values`) bleibt ROH: dort GIBT es keine Liste, die
  // etwas aufloesen koennte — „Search: all" ist ein Suchbegriff, keine
  // Kennung, und ein „(unknown)" daran waere schlicht falsch.
  return hatListe(d) ? `${roh} (unknown)` : roh
}

/** Der Grundzustand einer Dimension — „fehlt der Parameter, gilt die Vorgabe" (§10.1). */
function vorgabe(d) {
  const dflt = d.default
  switch (d.kind) {
    case 'multi':
    case 'quick':
      if (Array.isArray(dflt)) return dflt.slice()
      return dflt == null ? [] : [dflt]
    case 'switch':
      return dflt === true
    case 'date':
      return { from: dflt?.from ?? null, to: dflt?.to ?? null }
    case 'number':
      return { min: dflt?.min ?? null, max: dflt?.max ?? null }
    default:
      return dflt == null ? null : dflt
  }
}

/** Der „keine Einschraenkung"-Wert einer Dimension (das Ziel von `clear`). */
function leerWert(d) {
  switch (d.kind) {
    case 'multi':
    case 'quick': return []
    case 'switch': return false
    case 'date': return { from: null, to: null }
    case 'number': return { min: null, max: null }
    default: return null
  }
}

function normalisiere(d, wert) {
  if (wert == null) return leerWert(d)
  switch (d.kind) {
    case 'multi':
    case 'quick': return Array.isArray(wert) ? wert : [wert]
    case 'switch': return wert === true
    case 'date': return { from: wert.from ?? null, to: wert.to ?? null }
    case 'number': return { min: wert.min ?? null, max: wert.max ?? null }
    default: return wert
  }
}

/**
 * Engt diese Dimension die Liste ein? Nur einengende Kriterien bekommen einen Chip (§4.1).
 *
 * 🔴 `period` engt NIE ein: ein Rahmen-Segment legt fest, WIE Kennzahlen betrachtet
 * werden, statt Datensaetze auszublenden (§3.1 Frage 2, §3.2) — ein Zeitraum, der
 * Datensaetze ausblendet, ist ein `date`-Kriterium, kein Segment. Sein Geltungsbereich
 * steht deshalb am Segment selbst (§3.3 N3.6), nicht in Z3.
 */
function engtEin(d, wert) {
  switch (d.kind) {
    case 'multi':
    case 'quick': return Array.isArray(wert) && wert.length > 0
    case 'switch': return wert === true
    case 'date': return !!(wert && (wert.from || wert.to))
    case 'number': return !!(wert && (wert.min != null || wert.max != null))
    case 'period': return false
    default: return wert != null
  }
}

function gleich(a, b) {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => gleich(x, b[i]))
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a)
    const kb = Object.keys(b)
    return ka.length === kb.length && ka.every(k => gleich(a[k], b[k]))
  }
  return false
}

// ── Lesen ──────────────────────────────────────────────────────────────────

const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/

function leseAchse(params, achse, key, ignoriert) {
  const roh = params.get(key)
  if (roh == null || roh === '') return achse.default
  if (achse.values.length === 0 || achse.values.includes(roh)) return roh
  ignoriert.push({ key, value: roh, param: key, rest: null })
  return achse.default
}

function leseSeite(params) {
  const n = Number(params.get('page'))
  return Number.isInteger(n) && n > 1 ? n : 1
}

function leseWert(d, params, ignoriert) {
  const { key, kind } = d
  const erlaubt = rohWerte(d)
  const passt = v => erlaubt.length === 0 || erlaubt.includes(v)
  // 🔴 Der `all`-Marker gilt nur bei GESCHLOSSENER Werteliste. Freitext (kein
  // `values`) hat keine Liste, die `all` aufnehmen koennte — dort ist `all` ein
  // Suchbegriff. Ohne diese Bedingung verschwaende `?q=all` still (kein Chip,
  // keine Meldung, das Feld leerte sich selbst).
  const istMarker = erlaubt.length > 0 && !erlaubt.includes('all')

  if (kind === 'multi' || kind === 'quick') {
    // 🔴 G5a (2026-10-14, §3.2a Teil 1): eine Mehrfach-Dimension wird AUCH
    // WIEDERHOLT gelesen — `getAll`, nicht nur `get`.
    //
    // `frontend/src/home/components/UrgencyRow.jsx:128` öffnet
    // `/rma?status=open&status=approved&status=received`. Mit `get()` käme nur
    // `open` an, und `approved` und `received` verschwänden STILL: kein Chip,
    // keine Meldung — beide sind gültige Werte, sie werden nur nicht gelesen.
    // Dieselbe Form ist in `cases.md:772` als geteilter Link dokumentiert.
    //
    // Für eine einfache URL ist `getAll` dasselbe wie `get`; geschrieben wird
    // weiterhin komma-getrennt (`schreibeWert`). `single`/`switch`/`date`/
    // `number` bleiben bei `get` — der erste Wert gewinnt, unverändert: kein
    // gemessener Aufrufer schreibt dort wiederholt.
    const rohListe = params.getAll(key)
    if (rohListe.length === 0) return vorgabe(d)
    const zerlegt = rohListe
      .flatMap(s => s.split(','))
      .map(s => s.trim())
      .filter(Boolean)
    // `all` ist der Marker „ohne Einschränkung" — und er gilt nur, wenn er der
    // EINZIGE Wert ist. `?status=all&status=open` liefert `open` und meldet `all`
    // als unbekannten Wert: ein ausdrücklicher Wert schlägt den Marker. Sonst
    // hätte ein angehängtes `all` eine geteilte Auswahl still geleert.
    if (zerlegt.length === 1 && zerlegt[0] === 'all' && istMarker) return []
    const gut = []
    const schlecht = []
    // 🔴 Entdoppelt: `?status=open&status=open` ist keine zweite Auswahl. Ohne
    // das stünde „Status: Open, Open" im Chip und `open,open` in der URL.
    new Set(zerlegt).forEach(v => {
      if (passt(v)) gut.push(v)
      // 🔴 R16 W5 (G5a-Nachbesserung 5, 2026-10-14): `keepUnknown` haelt eine
      // Kennung, die die Liste nicht fuehrt, STATT sie zu verwerfen — der Chip
      // nennt sie dann „(unknown)" (`etikett`). Fuer „Responsible" ist die
      // Liste eine Teilmenge des Kennungsraums (Nutzer koennen geloescht
      // werden), und ein geteilter Link auf einen solchen Fall darf seinen
      // Filter nicht still verlieren. 🔴 Der `all`-Marker bleibt Marker: er ist
      // „ohne Einschraenkung", kein Wert, und wird auch unter `keepUnknown`
      // nicht zu einer Kennung.
      else if (d.keepUnknown && !(istMarker && v === 'all')) gut.push(v)
      else schlecht.push(v)
    })
    // Der Parameter traegt eine LISTE: bereinigt wird er auf die gueltigen Werte,
    // nicht geloescht — sonst fielen die gueltigen mit dem ungueltigen weg.
    const rest = gut.length > 0 ? gut.join(',') : null
    schlecht.forEach(v => ignoriert.push({ key, value: v, param: key, rest }))
    return gut
  }

  if (kind === 'date') {
    const von = params.get(`${key}_from`)
    const bis = params.get(`${key}_to`)
    if (von == null && bis == null) return vorgabe(d)
    const tag = (s, param) => {
      if (s == null) return null
      if (ISO_TAG.test(s)) return s
      ignoriert.push({ key, value: s, param, rest: null })
      return null
    }
    return { from: tag(von, `${key}_from`), to: tag(bis, `${key}_to`) }
  }

  if (kind === 'number') {
    const min = params.get(`${key}_min`)
    const max = params.get(`${key}_max`)
    if (min == null && max == null) return vorgabe(d)
    const zahl = (s, param) => {
      if (s == null) return null
      const n = Number(s)
      if (Number.isFinite(n)) return n
      ignoriert.push({ key, value: s, param, rest: null })
      return null
    }
    return { min: zahl(min, `${key}_min`), max: zahl(max, `${key}_max`) }
  }

  if (kind === 'switch') {
    const roh = params.get(key)
    if (roh == null) return vorgabe(d)
    if (roh === '1' || roh === 'true') return true
    if (roh === '0' || roh === 'false') return false
    ignoriert.push({ key, value: roh, param: key, rest: null })
    return vorgabe(d)
  }

  // single | period — ein leerer Wert ist „keine Einschraenkung", nicht ein Kriterium.
  const roh = params.get(key)
  if (roh == null || roh === '') return vorgabe(d)
  // s. o.: `all` ist nur Marker, wenn die Dimension `all` nicht selbst als Wert fuehrt.
  if (roh === 'all' && istMarker) return null
  if (passt(roh)) return roh
  ignoriert.push({ key, value: roh, param: key, rest: null })
  return vorgabe(d)
}

// ── Schreiben ──────────────────────────────────────────────────────────────

function schreibeAchse(params, key, wert, achse) {
  if (wert == null || wert === achse.default) params.delete(key)
  else params.set(key, String(wert))
}

function schreibeSeite(params, wert) {
  const n = Number(wert)
  if (!Number.isFinite(n) || n <= 1) params.delete('page')
  else params.set('page', String(Math.floor(n)))
}

function schreibeWert(params, d, wert) {
  const { key, kind } = d
  const basis = vorgabe(d)
  const setze = (k, v) => { if (v == null) params.delete(k); else params.set(k, String(v)) }

  if (kind === 'multi' || kind === 'quick') {
    const liste = Array.isArray(wert) ? wert : []
    if (gleich(liste, basis)) { params.delete(key); return }
    // „ausdruecklich ohne Einschraenkung" (§10.1) — sonst faellt der Wert auf die Vorgabe zurueck.
    if (liste.length === 0) { params.set(key, 'all'); return }
    params.set(key, liste.join(','))
    return
  }

  if (kind === 'date') {
    const w = wert ?? { from: null, to: null }
    if (gleich({ from: w.from ?? null, to: w.to ?? null }, basis)) {
      params.delete(`${key}_from`)
      params.delete(`${key}_to`)
      return
    }
    setze(`${key}_from`, w.from ?? null)
    setze(`${key}_to`, w.to ?? null)
    return
  }

  if (kind === 'number') {
    const w = wert ?? { min: null, max: null }
    if (gleich({ min: w.min ?? null, max: w.max ?? null }, basis)) {
      params.delete(`${key}_min`)
      params.delete(`${key}_max`)
      return
    }
    setze(`${key}_min`, w.min ?? null)
    setze(`${key}_max`, w.max ?? null)
    return
  }

  if (kind === 'switch') {
    const w = wert === true
    if (w === basis) { params.delete(key); return }
    params.set(key, w ? '1' : '0')
    return
  }

  // single | period — ein leerer Wert ist „keine Einschraenkung", nicht ein Kriterium.
  if (wert == null || wert === '') {
    if (basis == null) { params.delete(key); return }
    params.set(key, 'all')
    return
  }
  if (wert === basis) { params.delete(key); return }
  params.set(key, String(wert))
}

// ── Chips (Z3) ─────────────────────────────────────────────────────────────

function beschriftung(d, wert) {
  const { kind, label, unit, format = 'number' } = d
  // Die Einheit haengt am WERT, nicht am Chip: `Amount: ≥ 50,00 €` (§3.2).
  // 🔴 Hausform: NBSP vor der Einheit, nicht ein normales Leerzeichen — sonst
  // bricht die Zeile zwischen Wert und Einheit um und der Text ist nicht mehr
  // byte-gleich zu `formatUnit`/`formatMoney` (Regel-16-W1).
  const mitEinheit = s => (unit ? `${s}${NBSP}${unit}` : s)
  if (kind === 'multi') return `${label}: ${wert.map(v => etikett(d, v)).join(', ')}`
  if (kind === 'switch') return label
  if (kind === 'date') {
    if (wert.from && wert.to) return `${label}: ${formatDate(wert.from)} – ${formatDate(wert.to)}`
    if (wert.from) return `${label}: from ${formatDate(wert.from)}`
    return `${label}: until ${formatDate(wert.to)}`
  }
  if (kind === 'number') {
    // 🔴 Ueber die HAUSHELFER aus `utils/format.js`, nicht roh und nicht mit einem
    // eigenen Format: sonst stuende im Chip `1234.5`, wo die Tabellenzelle daneben
    // `1.234,50` zeigt — Filter und Tabelle spraechen zwei Formate (N3.9 verlangt
    // das Tabellenformat fuer Datumsangaben im Filter; fuer Zahlen gilt derselbe
    // Grund). 🔴 Und die ART waehlt den Helfer (Regel-16-K1): eine MENGE hat keine
    // Nachkommastellen (`formatQty`), ein BETRAG traegt Waehrung und NBSP selbst
    // (`formatMoney`, dessen Waehrungscode der `unit` ist).
    const geld = format === 'money'
    const zahl = geld
      ? v => formatMoney(v, { currency: unit || 'EUR' })
      : (format === 'qty' ? formatQty : formatNumber)
    // Bei Geld bringt der Helfer die Waehrung mit; sonst haengt die Einheit EINMAL
    // am Ende der Spanne (`50,00 – 100,00 €`, nicht zweimal).
    const spanne = s => (geld ? s : mitEinheit(s))
    if (wert.min != null && wert.max != null) return `${label}: ${spanne(`${zahl(wert.min)} – ${zahl(wert.max)}`)}`
    if (wert.min != null) return `${label}: ${spanne(`≥ ${zahl(wert.min)}`)}`
    return `${label}: ${spanne(`≤ ${zahl(wert.max)}`)}`
  }
  return `${label}: ${etikett(d, wert)}`
}

function baueChips(dimensions, state, set) {
  const chips = []
  dimensions.forEach(d => {
    const wert = state[d.key]
    if (!engtEin(d, wert)) return
    const istVorgabe = gleich(wert, vorgabe(d))
    const zusatz = istVorgabe ? ' · default' : ''
    // 🔴 Quick-Filter sind kombinierbar (§3.3 N3.3): JEDER gedrueckte Schluessel
    // bekommt seinen eigenen Chip, ✕ nimmt genau seinen heraus. Eine Facette
    // dagegen ist EIN Kriterium und traegt EINEN Chip mit allen Werten (§3.2).
    if (d.kind === 'quick') {
      wert.forEach(v => chips.push({
        id: `${d.key}:${v}`,
        label: `${etikett(d, v)}${zusatz}`,
        isDefault: istVorgabe,
        onRemove: () => set(d.key, wert.filter(x => x !== v)),
      }))
      return
    }
    chips.push({
      id: d.key,
      label: `${beschriftung(d, wert)}${zusatz}`,
      isDefault: istVorgabe,
      onRemove: () => set(d.key, null),
    })
  })
  return chips
}
